import React, { FC, useContext, useEffect } from 'react';
import { Formik, Form, FormikProps } from 'formik';
import { Row, Col } from 'antd';
import * as Yup from 'yup';
import styles from '../activityDataEntry.module.scss';
import InputField from 'shared/components/InputField';
import COSelect from 'shared/components/COSelect';
import { ProductionData } from 'models/ProductionData/productionData.model'; // Frontend-only
import { HelpDeskTooltipContext } from 'context/HelpDeskTooltipContext';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import useFilters from 'shared/hooks/useFilters';
import { queryParamsList } from 'views/ActivityDataEntry';
import { allowOnlyNumbers } from 'shared/utils/convertFormData';
import Category4Service from 'services/Category4Service/category4.service';

const currencyOptions = [
    { label: 'CAD ($)', value: 'CAD' },
    { label: 'USD ($)', value: 'USD' },
    { label: 'EUR (€)', value: 'EUR' },
];

interface ProductionDataComponentProps {
    formikRef?: React.RefObject<FormikProps<ProductionData>>;
    activeYear?: string;
    activeScope?: string;
    productionData?: ProductionData;
    locationId?: string;
    updateProductionData?: (
        locationId?: string,
        year?: string,
        payload?: ProductionData
    ) => Promise<void>;
    fetchYearForLocation?: (locationId?: string, year?: string) => Promise<void>;
    setIsEdit?: React.Dispatch<React.SetStateAction<boolean>>;
    isEditViewComponent?: boolean;
}

const productionDataValidationSchema = Yup.object().shape({
    revenue: Yup.number()
        .nullable()
        .min(0, 'Revenue must be a positive number'),
    revenueUnit: Yup.string()
        .nullable()
        .oneOf(['EUR', 'USD', 'CAD'], 'Invalid currency'),
    totalWeight: Yup.number()
        .nullable()
        .min(0, 'Total Weight must be a positive number'),
});

const ProductionDataComponent: FC<ProductionDataComponentProps> = ({
    formikRef,
    activeYear,
    activeScope,
    productionData,
    locationId,
    updateProductionData,
    fetchYearForLocation,
    setIsEdit,
}) => {
    const { getParams, setParams } = useFilters();
    const { isFirstFlow, isEditView } = getParams(queryParamsList);
    const { openTooltipModal } = useContext(HelpDeskTooltipContext);
    const { getCategory4Data, category4Data } = Category4Service();

    useEffect(() => {
        const fetchFinishedGoodsWeight = async () => {
            if (locationId && activeYear) {
                const yearNumber = Number(activeYear);
                // Only fetch if activeYear is a valid number (not location tab or other non-numeric tabs)
                if (!isNaN(yearNumber) && yearNumber > 0) {
                    await getCategory4Data({
                        locationId: locationId,
                        year: yearNumber,
                    });
                }
            }
        };
        fetchFinishedGoodsWeight();
    }, [locationId, activeYear]);

    // Check if activeYear is a valid number (not location tab or other non-numeric tabs)
    const isValidYear = activeYear && !isNaN(Number(activeYear)) && Number(activeYear) > 0;

    const getCurrencySymbol = (currency: string | null | undefined) => {
        switch (currency) {
            case 'EUR': return '€';
            case 'USD': return '$';
            case 'CAD': return '$';
            default: return '$';
        }
    };



    const initialValues: ProductionData = {
        revenue: productionData?.revenue ?? null,
        revenueUnit: productionData?.revenueUnit ?? 'CAD', // Use fetched value or default to CAD
        totalWeight: category4Data?.finishedGoods?.totalWeight ?? productionData?.totalWeight ?? null,
    };

    const handleSubmit = async (values: ProductionData) => {
        // Only submit if we have a valid year (not on location tab)
        if (!isValidYear) return;

        // Ensure revenueUnit is always included (use selected value or default to CAD)
        const submissionValues: ProductionData = {
            ...values,
            revenueUnit: values.revenueUnit || 'CAD', // Always include the selected currency unit
        };

        // Save to backend API (same pattern as scope1/scope2)
        // The selected currency unit from dropdown will be sent as revenue_unit to backend
        await updateProductionData?.(locationId, activeYear, submissionValues);
        setIsEdit?.(false);
        setParams(
            isFirstFlow
                ? { activeTab: activeYear, isFirstFlow: true }
                : { activeTab: activeYear },
            undefined,
            true
        );
        // Refresh data from backend after save (same pattern as scope1/scope2)
        await fetchYearForLocation?.(locationId, activeYear);
    };

    const handleToolTipOpen = (fieldName?: HelpDeskInputFieldEnum) => () => {
        fieldName && openTooltipModal(fieldName);
    };

    return (
        <Formik
            initialValues={initialValues}
            innerRef={formikRef}
            enableReinitialize
            validationSchema={productionDataValidationSchema}
            onSubmit={handleSubmit}>
            {({ values }) => (
                <Form>
                    <div className={styles.categoryContainer}>
                        <p className={styles.stationaryCategoryTitle}>Intensity Data</p>
                        <Row gutter={[24, 40]} className={styles.stationaryFormConatiner}>
                            <Col span={12}>
                                <div className={styles.fieldContainer}>
                                    <Col span={9}>
                                        <InputField
                                            name="revenue"
                                            placeholderstring={`Revenue`}
                                            label={`Revenue`}
                                            info
                                            infoClick={handleToolTipOpen(HelpDeskInputFieldEnum.revenue)}
                                            disabled={!isEditView}
                                            onKeyDown={allowOnlyNumbers}
                                            isNullable={true}
                                        />
                                    </Col>
                                    <Col span={7}>
                                        <COSelect
                                            name="revenueUnit"
                                            placeholderstring="Unit"
                                            label="Unit"
                                            options={currencyOptions}
                                            disabled={!isEditView}
                                            allowClear={false}
                                        />
                                    </Col>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div className={styles.fieldContainer}>
                                    <Col span={16}>
                                        <InputField
                                            name="totalWeight"
                                            placeholderstring="Total Weight (kg)"
                                            label="Total Weight (kg)"
                                            info
                                            infoClick={handleToolTipOpen(HelpDeskInputFieldEnum.productTotalWeight)}
                                            disabled={true}
                                            onKeyDown={allowOnlyNumbers}
                                            isNullable={true}
                                        />
                                    </Col>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default ProductionDataComponent;

